import {
  Project,
  Baseline,
  OneOfElements,
  IElementID,
  ProjectMetadata,
  FootprintMetadata,
  IElement,
  OneOfParentElements,
  ElementKind,
  ExpressionValue,
} from '../models/project.model';
import { Coordinate } from '@shared/models/geography.model';
import { clamp, toNumberObject } from './math.helpers';
import {
  forEachElement,
  getPathToElement,
  isBuildingVersionElement,
  isElement,
} from './recursive-element.helpers';
import { GetActivityExpressionVariables } from '../models/activities.model';
import {
  ACTIVITY_EXPRESSION_VARIABLES,
  GET_ACTIVITY_EXPRESSION_VARIABLES,
} from '../constants/activities.constants';
import { getActivityExpressionVariables } from './activities.helpers';
import {
  getKeys,
  mapFilterRecord,
  omit,
  omitUndefined,
  pick,
} from './object.helpers';
import { getVariablesInExpression } from './mathjs';
import {
  getElementAndQuantityProperties,
  getElementProperties,
  getCount,
  hasCount,
  isElementExpressionProperty,
  isElementSelectProperty,
  isElementSwitchProperty,
} from './element-property.helpers';
import {
  ElementPropertyResolvedCounts,
  IElementExpressionProperty,
  IElementProperty,
} from '../models/element-property.model';
import { isEqual, last } from 'lodash';
import { ActivitiesRecord, ActivityId } from '../models/activity-class.model';
import {
  errorHandledSolve,
  ExpressionErrorMessage,
} from './expression-solving.helpers';
import {
  emptyStoreySummedValues,
  getStoreySumExpressionVariables,
  IStoreySum,
  StoreySelector,
  withPlaceholderStoreyIfEmpty,
} from './storeys.helpers';
import {
  cacheFactory,
  getCacheFactoryDependencies,
  getCacheFactoryValue,
  required,
} from './function.helpers';
import { getProjectMeta, getVersionById } from './project.helpers';
import { toInternalGetStoreySelectors } from './design/expressions/get-storey-index.helpers';
import {
  expressionVariablesConstants,
  COORDINATE_STOCKHOLM,
} from '../constants/expression-variables.constants';
import {
  cloneReplaceItem,
  createRecordByKey,
  getEmptyFrozenArray,
} from './array.helpers';
import {
  createExpression,
  isExpressionValue,
} from './expression-factory.helpers';
import { IProduct, ProductID, ProductRecord } from '../models/product.model';
import { Results, quantityUnits } from '../models/unit.model';
import { getProductsInElement } from './product.helpers';
import { getProductConversionFactors } from './results.helpers';
import { isNameMatch, nameMatchTrim } from './string.helpers';
import { CALCULATIONS } from '../constants/calculations.constants';
import { reportSharedError } from './error-reporting.helpers';
import {
  elementHasMassRelatedQuantityExpressions,
  isElementQuantityName,
} from './element-quantity.helpers';
import {
  ElementQuantityExpressionRecord,
  IElementQuantityExpressionProperty,
} from '../models/element-quantities.model';
import { BUILDING_LIFETIME_DEFAULT } from './project-factory.helpers';
import {
  getElementCategory,
  getElementCategoryId,
} from './element-category.helpers';
import { genericProductsLookup } from '../generic-products';
import { getBuildingWidth } from './geometry/coordinates.helpers';
import {
  emptyStoreySummaryVariables,
  StoreySummaryVariables,
} from '@shared/models/design/design-expression-variables.model';
import { ElementCategoryID } from '../models/element-categories.model';
import { applyEnergyExpressionVariables } from './energy/energy-expression-variables.helpers';
import { getHeatedArea } from './energy/energy.helpers';
import { requiredKind } from './element-kind.helpers';
import { EnvironmentHourData } from './energy/weather.models';
import { EnergyExpressionVariables } from './energy/energy-expression-variables.models';
import { getElementsInProposal } from './proposal.helpers';
import { EXPRESSION_VARIABLE_NAME as NAME } from '@shared/constants/expression-variables-names.constant';
import { getSelectedDataTableItemFromProperty } from './data-tables';
import { DataTableDataItem, IDataTable } from '@shared/models/data-table.model';
import type { ProposalComputeRecord } from '@shared/models/pipeline/compute.model';
import type { IProposal } from '@shared/models/proposals.model';
import { guards } from './typeguards.helpers';
import { isTemporaryElementId } from './element.helpers';
import { getBuildingDesignFromProjectOrBuilding } from './design/design.helpers';
import { getDesignExpressionVariables } from './design/expressions/design-expression-variables.helpers';
import { designToProjectExpressionVariablesMigration } from './design/expressions/design-to-project-expression-variables.migration';
import {
  resolveStoreySelectors,
  toGetStoreySummary,
} from './design/expressions/storey-sum-from-design.helpers';
import { getResolvedVersionEnrichCacheDeps } from './actions/resolved-version-enrich-cache.helpers';
import { isRecipeScopedElementId } from './recipe.helpers';
import { withLegacyMetadataExpressionFields } from './design/legacy-metadata-expression-fields.helpers';
import { profiler } from './pipeline/pipeline-profiler';
import { getElementProductLifetime } from './results/lifetime.helpers';

type QuantityRecord = Record<string, number>;
type LegacyProjectExpressionStorey = {
  name?: string;
  gfa?: number;
  facade_edge?: number;
  mezzanine_edge?: number;
  inner_height?: number;
};

type LegacyProjectExpressionMetadata = ProjectMetadata & {
  activity_id?: ActivityId;
  below_ground?: number;
  building_lifetime?: number;
  storeys?: LegacyProjectExpressionStorey[];
  building_footprint?: FootprintMetadata;
  gfa_building?: number;
  building_perimeter?: number;
  activities?: Parameters<typeof getActivityExpressionVariables>[1];
};

export type ExpressionVariablesRecord = Record<IElementID, ExpressionVariables>;

/**
 * Resolved values for properties the user has explicitly set (`hasCount`) on an
 * element or any ancestor. Cascaded via {@link EXPRESSION_VARIABLE_NAME.USER_DEFINED}
 * so helpers like {@link getSBEFLifetime} can prefer user intent over product/SBEF
 * fallbacks without treating inherited fallback counts as user-set.
 */
export type UserDefinedExpressionVariables = Partial<
  Record<string, ElementPropertyResolvedCounts>
>;

/**
 * Dash (-) and dot (.) can't be used in expressions (minus operator / property access).
 * So we replace them with an underscore (_).
 */
type ExpressionResults = Required<
  Omit<
    Results,
    | 'sek_A1-A3'
    | 'sek_B1-B7'
    | 'co2e_A1-A3'
    | 'co2e_B1.1'
    | 'co2e_B1.2'
    | 'm³'
    | 'kg/m³'
    | 'kg/m²'
    | 'm²'
  >
> & {
  sek_A1_A3: number;
  sek_B1_B7: number;
  co2e_A1_A3: number;
  co2e_B1_1: number;
  co2e_B1_2: number;
  m3: number;
  kg_m3: number;
  kg_m2: number;
  m2: number;
};

type IsActivityFn = (id: number) => 1 | 0;
type GetActivityFn = (type: string) => Record<string, number | undefined>;
/** 1-based storey selectors (`getStorey(1)` is the first storey). Index 0 throws. */
type GetStoreyFn = (...selectors: StoreySelector[]) => StoreySummaryVariables;
type GetProductFn = (productIdOrName: string) => IProduct | undefined;
type GetConversionFactorsFn = (productIdOrName: string) => ExpressionResults;
type GetProductDensityFn = (productIdOrName: string) => number;
type GetProductDensityPerSqmFn = (productIdOrName: string) => number;

/** Storey keys kept on {@link defaultVariables} (still referenced in stored expressions). */
const DEFAULT_VARIABLE_STOREY_KEYS = [
  'gfa',
  'inner_height',
  'perimeter',
  'total_height',
  'external_gwa',
  'external_gwa_above_ground',
  'apartment_count',
  'stairwell_count',
  'occupant_count',
  'gfa_floor_slabs',
  'gfa_ground_floor_slabs',
  'gfa_sky',
] as const satisfies ReadonlyArray<keyof IStoreySum>;

const pickDefaultVariableStoreyValues = (
  storeySum: IStoreySum,
): Pick<IStoreySum, (typeof DEFAULT_VARIABLE_STOREY_KEYS)[number]> =>
  pick(storeySum, ...DEFAULT_VARIABLE_STOREY_KEYS);

/** Full legacy storey sum on version scope (charts / internal; not in defaultVariables). */
export const pickVersionStoreySumValues = (
  storeySum: IStoreySum,
): Omit<IStoreySum, 'name'> => omit(storeySum, 'name');

export { toGetStoreySummary };

export interface ExpressionVariables extends Partial<EnergyExpressionVariables> {
  gfa: number;
  inner_height: number;
  perimeter: number;
  total_height: number;
  external_gwa: number;
  external_gwa_above_ground: number;
  apartment_count: number;
  stairwell_count: number;
  occupant_count: number;
  gfa_floor_slabs: number;
  gfa_ground_floor_slabs: number;
  gfa_sky: number;
  below_ground: number;
  gfa_elevator_per_stairwell_per_level: number;
  piling_count: number;
  activity_electricity: number;
  heating_demand: number;
  heating_demand_tapwater: number;
  activities: ActivitiesRecord;
  /** Set on version scope for category templates (e.g. AHU); not in {@link defaultVariables} */
  activity?: number;
  building_footprint: number;
  building_width: number;
  building_height: number;
  building_perimeter: number;
  building_position: Coordinate;
  storeys_count: number;
  [NAME.HEATED_AREA]: number;
  [NAME.BUILDING_LIFETIME]?: number;
  parent?: ExpressionVariables;
  id?: IElementID;
  getStorey: GetStoreyFn;
  /** Version scope only (legacy activity GFA); not in {@link defaultVariables} */
  getActivity?: GetActivityFn;
  getProductDensity: GetProductDensityFn;
  getProductDensityPerSqm: GetProductDensityPerSqmFn;
  isActivity: IsActivityFn;
  /** Set by {@link applyProductVariables}; not in {@link defaultVariables} */
  productIds?: ProductID[];
  densityRecord?: Record<ProductID, number>;
  densityArray?: number[];
  densityPerSqmRecord?: Record<ProductID, number>;
  densityPerSqmArray?: number[];
  [NAME.ACTIVE_ELEMENTS_COUNT_BY_CATEGORY]: ReturnType<
    typeof getActiveElementsCountByCategory
  >;

  /**
   * Record of property values the user has explicitly set (`hasCount`) on this
   * element or any ancestor. Keys are expression variable names; nearest
   * ancestor wins. See {@link EXPRESSION_VARIABLE_NAME.USER_DEFINED}.
   */
  [NAME.USER_DEFINED]?: UserDefinedExpressionVariables;

  /**
   * Allow ANY key (element properties, optional product density arrays, etc.)
   */
  [key: string]:
    | ElementPropertyResolvedCounts
    | ActivitiesRecord
    | IsActivityFn
    | GetActivityFn
    | GetProductDensityFn
    | GetProductDensityPerSqmFn
    | GetStoreyFn
    | QuantityRecord
    | ProductID[]
    | ExpressionVariables
    | number[]
    | undefined
    | Coordinate
    | Record<string, string>
    | Record<ProductID, number>
    | UserDefinedExpressionVariables
    | ReturnType<typeof getActiveElementsCountByCategory>
    | DataTableDataItem;
}

/**
 * Empty active elements count by category. Used to avoid undefined errors in mathjs expressions.
 */
const EMPTY_ACTIVE_ELEMENTS_COUNT_BY_CATEGORY: Record<
  ElementCategoryID,
  number
> = Object.values(ElementCategoryID).reduce(
  (acc, id) => {
    acc[id] = 0;
    return acc;
  },
  {} as Record<ElementCategoryID, number>,
);

/**
 * Default expression variables. All expression variables that will be available from geometry etc set to zero values
 */
export const defaultVariables: Readonly<ExpressionVariables> = {
  ...pickDefaultVariableStoreyValues(emptyStoreySummedValues),
  activities: expressionVariablesConstants.activities,
  isActivity: () => 1,
  getStorey: () => ({ ...emptyStoreySummaryVariables }),
  getProductDensity: () => 0,
  getProductDensityPerSqm: () => 0,
  below_ground: 0,
  gfa_elevator_per_stairwell_per_level: 0,
  piling_count: 0,
  activity_electricity: 0,
  heating_demand: 0,
  heating_demand_tapwater: 0,
  building_footprint: 0,
  [NAME.BUILDING_LIFETIME]: 50,
  building_width: 0,
  building_height: 0,
  building_perimeter: 0,
  building_position: COORDINATE_STOCKHOLM,
  [NAME.HEATED_AREA]: 0,
  storeys_count: 0,
  [NAME.ACTIVE_ELEMENTS_COUNT_BY_CATEGORY]: {
    ...EMPTY_ACTIVE_ELEMENTS_COUNT_BY_CATEGORY,
  },
};
const defaultKeys = getKeys(defaultVariables);

/**
 * Format a property name to a valid expression variable name.
 * @param name
 * @returns
 */
export const formatExpressionVariableName = (name: string): string => {
  // First trim leading spaces only
  const trimmedName = name.replace(/^\s+/g, '');
  // Then replace all remaining spaces with underscores
  return trimmedName.replace(/\s/g, '_').toLowerCase();
};

/**
 * Maps pipeline compute items to variable keys ({@link formatExpressionVariableName}) so step 3.5 can skip re-solving the same property expressions.
 */
const buildPipelinePropertyOverridesByElementId = (
  record: ProposalComputeRecord,
): Map<IElementID, Record<string, number>> => {
  const map = new Map<IElementID, Record<string, number>>();
  for (const item of Object.values(record.items)) {
    const props = Object.entries(item.properties);
    if (!props.length) continue;
    const formatted: Record<string, number> = {};
    for (const [name, value] of props) {
      formatted[formatExpressionVariableName(name)] = value;
    }
    map.set(item.id, formatted);
  }
  return map;
};

export type GetExpressionVariablesRecordOptions = {
  /** When set (after {@link enrichFromComputeRecord}), reuse resolved numeric properties instead of {@link errorHandledSolve} where keys match */
  pipelineComputeRecord?: ProposalComputeRecord;
  /** When set, heating/cooling demand variables match the active proposal (see {@link getEnergyExpressionVariables}). */
  proposal?: IProposal;
};

type BuildExpressionVariablesRecordParams = {
  project: Project;
  resolvedVersion: Baseline;
  resolvedScope: OneOfParentElements;
  weatherHours: EnvironmentHourData[];
  dataTables: IDataTable[];
  proposal?: IProposal;
  pipelineComputeRecord?: ProposalComputeRecord;
  /** When set, only compute element rows in this set (must include version id and ancestors of targets). Not cached. */
  onlyElementIds?: ReadonlySet<string>;
};

/**
 * Cache key shared by {@link getExpressionVariablesRecord} and recipe enrich reuse.
 */
export const getExpressionVariablesRecordCacheKey = (
  versionId: string,
  proposalId?: string,
): string =>
  `getExpressionVariablesRecord[${versionId}][${proposalId ?? 'baseline'}]`;

/**
 * Element ids that must exist in a variables record for proposal recipe auto-quantity in {@link enrichResolvedTree}:
 * recipe-scoped nodes (`:r:`) in the active enrich branches, plus every ancestor on their path
 * (inheritance for `autoUpdateElementQuantity`), and the version root row.
 *
 * @returns `undefined` when no recipe targets need quantity sync — caller can skip expr work entirely.
 */
export const collectExpressionVariableIdsForRecipeEnrich = (
  version: Baseline,
  enrichBranches?: ReadonlySet<string>,
): ReadonlySet<string> | undefined => {
  const needed = new Set<string>([version.id]);
  let hasRecipeTarget = false;

  forEachElement(version, (element) => {
    if (!isElement(element) || !isRecipeScopedElementId(element.id)) return;
    if (enrichBranches && !enrichBranches.has(element.id)) return;
    hasRecipeTarget = true;
    needed.add(element.id);
    // Parent chain must be computed before the recipe child row (quantity fallbacks inherit upstream).
    const path = getPathToElement(version, element.id, false);
    if (path) {
      for (const ancestor of path) needed.add(ancestor.id);
    }
  });

  return hasRecipeTarget ? needed : undefined;
};

/**
 * Element ids for proposal auto-quantity on non-recipe elements in enrich branches
 * (e.g. other-product with {@link ProjectChangeType.ElementQuantityValue} on area_top — DEV-3349).
 */
const collectExpressionVariableIdsForProposalQuantityEnrich = (
  version: Baseline,
  enrichBranches?: ReadonlySet<string>,
): ReadonlySet<string> | undefined => {
  if (!enrichBranches?.size) return undefined;

  const needed = new Set<string>([version.id]);
  let hasTarget = false;

  forEachElement(version, (element) => {
    if (!isElement(element) || isRecipeScopedElementId(element.id)) return;
    if (!enrichBranches.has(element.id)) return;
    if (!elementHasMassRelatedQuantityExpressions(element)) return;

    hasTarget = true;
    needed.add(element.id);
    const path = getPathToElement(version, element.id, false);
    if (path) {
      for (const ancestor of path) needed.add(ancestor.id);
    }
  });

  return hasTarget ? needed : undefined;
};

const mergeQuantityEnrichElementIds = (
  ...sets: (ReadonlySet<string> | undefined)[]
): ReadonlySet<string> | undefined => {
  const merged = new Set<string>();
  for (const set of sets) {
    if (!set) continue;
    for (const id of set) merged.add(id);
  }
  return merged.size > 0 ? merged : undefined;
};

/**
 * Recipe + proposal quantity targets that need expression variables in {@link enrichResolvedTree}.
 */
const collectExpressionVariableIdsForQuantityEnrich = (
  version: Baseline,
  enrichBranches?: ReadonlySet<string>,
): ReadonlySet<string> | undefined =>
  mergeQuantityEnrichElementIds(
    collectExpressionVariableIdsForRecipeEnrich(version, enrichBranches),
    collectExpressionVariableIdsForProposalQuantityEnrich(
      version,
      enrichBranches,
    ),
  );

/**
 * True when {@link enrichResolvedTree} must re-run auto-quantity (recipe and/or proposal quantity edits).
 */
export const hasQuantityEnrichTargets = (
  version: Baseline,
  enrichBranches?: ReadonlySet<string>,
): boolean =>
  collectExpressionVariableIdsForQuantityEnrich(version, enrichBranches) !==
  undefined;

/**
 * Builds a variables record (version row + per-element rows). Used by the cached UI path and by
 * uncached narrow enrich — see {@link onlyElementIds} on {@link BuildExpressionVariablesRecordParams}.
 */
const buildExpressionVariablesRecord = ({
  project,
  resolvedVersion,
  resolvedScope,
  weatherHours,
  dataTables,
  proposal,
  pipelineComputeRecord,
  onlyElementIds,
}: BuildExpressionVariablesRecordParams): ExpressionVariablesRecord => {
  const meta = getProjectMeta(project);
  const baseVersionVars = getVersionExpressionVariables(
    project,
    resolvedVersion,
  );
  const design = getBuildingDesignFromProjectOrBuilding(project);

  // Energy vars always use baseline tree + proposal; quantity overrides live on the resolved tree only.
  const baselineVersion =
    getVersionById(project, resolvedVersion.id) ?? resolvedVersion;
  const versionVars = applyEnergyExpressionVariables(
    baselineVersion,
    meta,
    baseVersionVars,
    weatherHours,
    design,
    proposal,
  );

  const expressions: ExpressionVariablesRecord = {
    [resolvedVersion.id]: versionVars,
  };

  const pipelineOverridesByElementId = pipelineComputeRecord
    ? buildPipelinePropertyOverridesByElementId(pipelineComputeRecord)
    : undefined;

  const scopeVars = versionVars;
  // Generic catalog is the base product resolution; version products override it. Matches
  // getVersionExpressionVariables and computeVersionForProposal so density/areal-density vars
  // resolve even when proposal enrich runs before version.products is synced (DEV-3349).
  const productRecord: ProductRecord = {
    ...genericProductsLookup,
    ...resolvedVersion.products,
  };

  forEachElement(resolvedScope, (element, path) => {
    if (isBuildingVersionElement(element)) return;
    // Narrow enrich: skip elements off the recipe branch, but keep every id in onlyElementIds (incl. ancestors).
    if (onlyElementIds && !onlyElementIds.has(element.id)) return;

    const parent = last(path);
    const parentVariables = (parent && expressions[parent?.id]) ?? scopeVars;

    expressions[element.id] = getElementExpressionVariables(
      element,
      parentVariables,
      productRecord,
      dataTables,
      pipelineOverridesByElementId?.get(element.id),
    );
  });

  return expressions;
};

/**
 * Expression variables for {@link enrichResolvedTree} quantity auto-update (recipe children and
 * proposal quantity edits such as other-product area overrides — DEV-3349).
 *
 * Reuses {@link getExpressionVariablesRecord}'s cache only when that record was
 * built for this resolved tree. The cache key is version id + proposal id, so a
 * hit can still describe the previous tree (cast-in-place density after a
 * hollow-core swap, DEV-3915). The side panel prices `count.resolved` from
 * these variables.
 *
 * Otherwise a narrow uncached build via {@link buildExpressionVariablesRecord} and
 * {@link collectExpressionVariableIdsForQuantityEnrich}. Must not write a partial record into
 * `cacheFactory` — UI hooks need the full tree and will populate the shared key once.
 */
export const getExpressionVariablesRecordForRecipeEnrich = (
  project: Project,
  resolvedVersion: Baseline,
  options?: {
    proposal?: IProposal;
    enrichBranches?: ReadonlySet<string>;
    weatherHours?: EnvironmentHourData[];
    dataTables?: IDataTable[];
  },
): ExpressionVariablesRecord => {
  const weatherHours = options?.weatherHours ?? getEmptyFrozenArray();
  const dataTables = options?.dataTables ?? getEmptyFrozenArray();
  const proposalCacheKey = options?.proposal?.id ?? 'baseline';
  const cacheKey = getExpressionVariablesRecordCacheKey(
    resolvedVersion.id,
    proposalCacheKey,
  );
  const cachedDeps = getCacheFactoryDependencies(cacheKey);
  const cached = getCacheFactoryValue<ExpressionVariablesRecord>(cacheKey);
  // getExpressionVariablesRecord stores the resolved version and its actions first.
  const cachedForThisTree =
    cachedDeps?.[0] === resolvedVersion &&
    cachedDeps?.[1] === resolvedVersion.actions;
  if (cached && cachedForThisTree) return cached;

  const onlyElementIds = collectExpressionVariableIdsForQuantityEnrich(
    resolvedVersion,
    options?.enrichBranches,
  );
  // Defensive fallback (callers should gate with hasQuantityEnrichTargets); version row only.
  if (!onlyElementIds) {
    return buildExpressionVariablesRecord({
      project,
      resolvedVersion,
      resolvedScope: resolvedVersion,
      weatherHours,
      dataTables,
      proposal: options?.proposal,
      onlyElementIds: new Set([resolvedVersion.id]),
    });
  }

  // Intentionally not cached — storing a subset under the full cache key would break hook consumers.
  return buildExpressionVariablesRecord({
    project,
    resolvedVersion,
    resolvedScope: resolvedVersion,
    weatherHours,
    dataTables,
    proposal: options?.proposal,
    onlyElementIds,
  });
};

/**
 * @deprecated Use getDesignExpressionVariables instead
 * Get resolved base variables for project.
 * These will be used to resolve formulas later
 * the acronym LA stands for "living area" and GFA is "gross floor area"
 * @param meta Project metadata
 * @param products Products used in version
 * @returns Updated project
 */
export const getProjectExpressionVariables = (
  meta: LegacyProjectExpressionMetadata,
  products: ProductRecord = {},
): ExpressionVariables => {
  const activity = meta.activity_id || ActivityId.PrivateHousing;
  const building_position = getLocationFromProjectMetadata(meta);
  // Inputs from legacy project metadata.
  const mergedMeta = {
    ...defaultVariables,
    [NAME.BUILDING_LIFETIME]: BUILDING_LIFETIME_DEFAULT,
    ...omitUndefined(meta),
  };
  const {
    below_ground,
    building_lifetime,
    storeys: mergedStoreys,
  } = mergedMeta;

  const metaForStoreys = withPlaceholderStoreyIfEmpty({
    ...meta,
    storeys: mergedStoreys,
  });
  const storeys = metaForStoreys.storeys;

  const { building_footprint } = meta;
  const { area } = building_footprint ?? {};
  const building_width = getBuildingWidth(building_footprint?.coordinates);
  const storeys_count = storeys.length;

  const storeySumExpressionVariables = getStoreySumExpressionVariables(
    metaForStoreys,
    undefined,
  ).sum;

  const gfaFromResolvedStoreys = storeySumExpressionVariables.gfa;
  const gfaFromDesignOrFootprint = getBuildingGFA(metaForStoreys);
  const gfa =
    gfaFromResolvedStoreys > 0
      ? Math.max(gfaFromDesignOrFootprint, gfaFromResolvedStoreys)
      : gfaFromDesignOrFootprint;

  const sumGFAForActivities =
    gfaFromResolvedStoreys > 0 ? gfaFromResolvedStoreys : gfa;

  const atemp = getHeatedArea(gfa);

  const {
    perimeter,
    total_height,
    apartment_count,
    stairwell_count,
    occupant_count,
  } = storeySumExpressionVariables;

  const activityExpressionValues = meta.activities
    ? getActivityExpressionVariables(
        {
          gfa,
          sumGFA: sumGFAForActivities,
        },
        meta.activities,
      )
    : ACTIVITY_EXPRESSION_VARIABLES;

  const {
    apartments_balcony_area_per_apartment,
    apartments_bathroom_area_per_apartment,
    apartments_living_area_per_apartment,
    bike_room_gfa,
    building_utilities_gfa,
    garage_gfa,
    laundry_room_gfa,
    stairwells_apartments_per_stairwell_per_storey,
    stairwells_elevators_per_stairwell,
    storage_gfa,
  } = activityExpressionValues;

  const building_height = total_height;

  const gfa_elevator_per_stairwell_per_level =
    stairwells_elevators_per_stairwell *
    expressionVariablesConstants.gfa_per_elevator_per_level;

  const piling_count =
    expressionVariablesConstants.piling_count_per_building_gfa * gfa;

  const activity_electricity = CALCULATIONS.activity_electricity.calculate({
    occupant_count,
    activity,
  });

  const isActivity: IsActivityFn = (id: number): 1 | 0 =>
    id === activity ? 1 : 0;

  // Blank selectors (undefined / '') mean all storeys, same as design overlay getStorey.
  const getStorey: GetStoreyFn = (...selectors: StoreySelector[]) =>
    toGetStoreySummary(
      getStoreySumExpressionVariables(
        metaForStoreys,
        toInternalGetStoreySelectors(resolveStoreySelectors(selectors)),
      ).sum,
    );

  const getActivity: GetActivityFn = (type) => {
    if (
      !Object.keys(GET_ACTIVITY_EXPRESSION_VARIABLES).includes(
        type as keyof GetActivityExpressionVariables,
      )
    ) {
      return {};
    }

    const variables = {
      apartments: {
        living_area_per_apartment: apartments_living_area_per_apartment,
        balcony_area_per_apartment: apartments_balcony_area_per_apartment,
        bathroom_area_per_apartment: apartments_bathroom_area_per_apartment,
      },
      stairwells: {
        apartments_per_stairwell_per_storey:
          stairwells_apartments_per_stairwell_per_storey,
        elevators_per_stairwell: stairwells_elevators_per_stairwell,
      },
      laundry_room: { gfa: laundry_room_gfa },
      bike_room: { gfa: bike_room_gfa },
      storage: { gfa: storage_gfa },
      building_utilities: { gfa: building_utilities_gfa },
      garage: { gfa: garage_gfa },
    };

    return variables[type as keyof GetActivityExpressionVariables];
  };

  const getProduct: GetProductFn = (productIdOrName) =>
    getProductsFn(products, productIdOrName);

  const getConversionFactors: GetConversionFactorsFn = (productIdOrName) =>
    getConversionFactorsFn(getProduct, productIdOrName);

  const getProductDensity: GetProductDensityFn = (productIdOrName) =>
    getProductDensityFn(getConversionFactors, productIdOrName);

  const getProductDensityPerSqm: GetProductDensityPerSqmFn = (
    productIdOrName,
  ) => getProductDensityPerSqmFn(getConversionFactors, productIdOrName);

  return applyProductVariables(
    {
      ...defaultVariables,
      ...pickVersionStoreySumValues(storeySumExpressionVariables),
      building_position,
      activity,
      isActivity,
      getStorey,
      getActivity,
      getProductDensity,
      getProductDensityPerSqm,
      ...toNumberObject({
        apartment_count,
        [NAME.HEATED_AREA]: atemp,
        below_ground,
        [NAME.BUILDING_LIFETIME]: building_lifetime,
        building_footprint: area,
        building_height,
        building_perimeter: perimeter,
        gfa,
        gfa_elevator_per_stairwell_per_level,
        piling_count,
        stairwell_count,
        storeys_count,
        building_width,
        activity_electricity,
      }),
    },
    Object.values(products),
  );
};

/**
 * Apply product variables to expression variables
 * @param variables
 * @param products Need to be in order of usage
 * @returns
 */
export const applyProductVariables = (
  variables: ExpressionVariables,
  products: IProduct[],
): ExpressionVariables => {
  const productRecord = createRecordByKey(products, 'id');
  const densityRecord = getDensityRecord(productRecord);
  const densityPerSqmRecord = getDensityPerSqmRecord(productRecord);
  const productIds = products
    .map((p) => p.id)
    .filter((id) => densityRecord[id] || densityPerSqmRecord[id]);

  // No need to update => return original
  if (isEqual(variables.productIds, productIds)) {
    return variables;
  }

  const densityArray = productIds
    .map((id) => densityRecord[id] ?? 0)
    .filter((d) => !!d);
  const densityPerSqmArray = productIds
    .map((id) => densityPerSqmRecord[id] ?? 0)
    .filter((d) => !!d);

  // Make sure we have at least two values for each array
  while (densityArray.length < 2) {
    densityArray.push(0);
  }
  while (densityPerSqmArray.length < 2) {
    densityPerSqmArray.push(0);
  }

  return {
    ...variables,
    densityRecord,
    densityArray,
    densityPerSqmRecord,
    densityPerSqmArray,
    productIds,
  };
};

/**
 * Get the expression variables for a version.
 * This is the root of all other expression variables.
 * Will only return new variables when version or meta data is changed
 * @param version
 * @returns
 */
const getVersionExpressionVariables = (
  project: Project,
  version: Baseline,
): ExpressionVariables => {
  const meta = getProjectMeta(project);
  const design = required(
    getBuildingDesignFromProjectOrBuilding(project),
    'building design is required',
  );

  // Cache once per version and only run when version or design data is changed.
  return cacheFactory(
    () => {
      const products = {
        ...genericProductsLookup,
        ...version.products,
      };
      const projectVars = withLegacyMetadataExpressionFields(
        meta,
        designToProjectExpressionVariablesMigration(
          getDesignExpressionVariables(design),
        ) as ExpressionVariables,
      );

      const versionVars = getElementExpressionVariables(
        version,
        projectVars as ExpressionVariables,
        products,
      );

      return {
        ...projectVars,
        ...versionVars,
        [NAME.ACTIVE_ELEMENTS_COUNT_BY_CATEGORY]:
          getActiveElementsCountByCategory(version),
      };
    },
    `getVersionExpressionVariables[${version.id}]`,
    [version, meta, design],
  );
};

/**
 * Get the number of active elements in selected proposal by category
 * @param version
 * @returns A record containing the number of active elements in selected proposal by category
 */
export const getActiveElementsCountByCategory = (
  version: Baseline,
): Record<ElementCategoryID, number> => {
  return getElementsInProposal(version).reduce(
    (acc, element) => {
      const categoryId = getElementCategoryId(element);
      if (categoryId && !isTemporaryElementId(element.id)) {
        acc[categoryId] = (acc[categoryId] ?? 0) + 1;
      }
      return acc;
    },
    { ...EMPTY_ACTIVE_ELEMENTS_COUNT_BY_CATEGORY },
  );
};

export const getLocationFromProjectMetadata = (
  meta: LegacyProjectExpressionMetadata,
): Coordinate => {
  return (
    meta.building_footprint?.coordinates[0] ??
    defaultVariables.building_position
  );
};
/**
 * Get a record mapping variables for each element in a version.
 * Child elements will also inherit all variables from it's parent.
 * @param project
 * @param resolvedVersion Version whose element tree is walked (baseline from project or output of {@link resolveVersionForProposal}).
 * @param scope Provide a scope to limit the search to a specific part of the version (for performance reasons). Will use resolvedVersion if not provided.
 * @param options Optional {@link GetExpressionVariablesRecordOptions} (e.g. pipeline compute reuse after enrich).
 * @returns
 */
export const getExpressionVariablesRecord = (
  project: Project,
  resolvedVersion: Baseline,
  weatherHours: EnvironmentHourData[] = getEmptyFrozenArray(),
  scope?: OneOfParentElements,
  dataTables: IDataTable[] = getEmptyFrozenArray(),
  options?: GetExpressionVariablesRecordOptions,
): ExpressionVariablesRecord => {
  profiler.time('getExpressionVariablesRecord');
  profiler.count('getExpressionVariablesRecord:calls');

  const resolvedScope = scope ?? resolvedVersion;
  const proposalCacheKey = options?.proposal?.id ?? 'baseline';
  const projectEnrichDeps = getResolvedVersionEnrichCacheDeps(project);

  // Energy + per-element vars run inside cacheFactory so callers share one record via stable deps
  // (versionVars must not be a dep — applyEnergyExpressionVariables returns a new object every time).
  const result = cacheFactory(
    () =>
      buildExpressionVariablesRecord({
        project,
        resolvedVersion,
        resolvedScope,
        weatherHours,
        dataTables,
        proposal: options?.proposal,
        pipelineComputeRecord: options?.pipelineComputeRecord,
      }),
    getExpressionVariablesRecordCacheKey(resolvedScope.id, proposalCacheKey),
    // scope + dataTables must invalidate the cache: subtree and table-driven vars change independently of version/meta refs.
    // Use enrich cache deps (not `project` ref) and proposal id — same rules as {@link getResolvedBuildingVersion}.
    [
      resolvedVersion,
      resolvedVersion.actions,
      ...projectEnrichDeps,
      weatherHours,
      resolvedScope,
      dataTables,
      options?.pipelineComputeRecord ?? null,
      proposalCacheKey,
    ],
  );
  profiler.timeEnd('getExpressionVariablesRecord');
  return result;
};

/**
 * Build the cascaded {@link EXPRESSION_VARIABLE_NAME.USER_DEFINED} record: inherit
 * ancestor entries, then overwrite with any property on this element that has an
 * explicit user-set {@link hasCount} (nearest wins).
 */
const buildUserDefinedExpressionVariables = (
  properties: IElementProperty[],
  variables: ExpressionVariables,
  parentUserDefined: UserDefinedExpressionVariables | undefined,
): UserDefinedExpressionVariables => {
  const userDefined: UserDefinedExpressionVariables = {
    ...parentUserDefined,
  };

  for (const property of properties) {
    if (!hasCount(property)) continue;
    const key = formatExpressionVariableName(property.name);
    const resolved = variables[key];
    if (!guards.is.defined(resolved)) continue;
    // Only persist resolved property counts (expression / select / switch), not helpers
    if (
      guards.is.number(resolved) ||
      guards.is.string(resolved) ||
      guards.is.boolean(resolved)
    ) {
      userDefined[key] = resolved;
    }
  }

  return userDefined;
};

/** One report per element + cycle shape — element variables recompute on every edit. */
const reportedCycleSignatures = new Set<string>();

/**
 * Report circular property references to the host runtime (Sentry via
 * {@link reportSharedError}). Only cycles involving a system-assigned
 * `fallbackCount` (a member without a user {@link hasCount}) are reported —
 * the system wrote an edge the user never asked for (DEV-3642). User-written
 * cycles are surfaced in the UI instead.
 */
const reportSystemCausedCycles = (
  elementId: IElementID,
  cycles: string[][],
  properties: IElementProperty[],
): void => {
  const userCountNames = new Set(
    properties
      .filter(hasCount)
      .map((p) => formatExpressionVariableName(p.name)),
  );
  const systemCausedCycles = cycles.filter((cycle) =>
    cycle.some((name) => !userCountNames.has(name)),
  );
  if (systemCausedCycles.length === 0) return;

  const formattedCycles = systemCausedCycles.map(formatExpressionCycle);
  const signature = `${elementId}:${formattedCycles.join('|')}`;
  if (reportedCycleSignatures.has(signature)) return;
  reportedCycleSignatures.add(signature);

  reportSharedError(new Error(ExpressionErrorMessage.Circular), {
    elementId,
    cycles: formattedCycles,
  });
};

/**
 * Get available variables for a specific element. Includes both element properties and quantity properties.
 * @param element
 * @param parentVariables Variables element should inherit from parents. Element will overwrite any vars with equal keys.
 * @param productRecord Record of products to use for the element expression variables.
 * @returns
 */
export const getElementExpressionVariables = (
  element: OneOfElements,
  parentVariables: ExpressionVariables | undefined,
  productRecord: ProductRecord = {},
  dataTables: IDataTable[] = [],
  pipelineResolvedPropertyKeys?: Record<string, number>,
): ExpressionVariables => {
  return cacheFactory(
    () => {
      /* 
      Only include ids for products that have conversion factors that can used to calculate density
      (except for insulation products that are missing volume). TODO: Is this working still?=
      */

      const products = getProductsInElement(element, productRecord);

      // Enrich variables with recent product variables (like densityArray, etc.)
      const variables = mapExpressionVariablesByElementCategoryBefore(
        applyProductVariables(
          {
            ...defaultVariables,
            ...parentVariables,
            parent: parentVariables,
            id: element.id,
          },
          products,
        ),
        element,
      );

      // Expose the product service-life override so getSBEFLifetime ranks it above the
      // SBEF table / parent inheritance for the `lifetime` property fallback
      const productLifetime = getElementProductLifetime(element);
      if (productLifetime !== undefined) {
        variables[NAME.PRODUCT_LIFETIME] = productLifetime;
      }

      // Filter out properties that should be resolved before project variables
      const properties = getElementAndQuantityProperties(element);
      const { variables: resolvedVariables, cycles } =
        getExpressionVariablesAndCyclesFromProperties(
          properties,
          variables,
          dataTables,
          pipelineResolvedPropertyKeys,
        );

      if (cycles.length > 0) {
        reportSystemCausedCycles(element.id, cycles, properties);
      }

      // Cascade explicitly set property values so descendants can prefer user intent
      // over product/SBEF fallbacks (e.g. lifetime vs IVL — DEV-3549).
      const userDefined = buildUserDefinedExpressionVariables(
        properties,
        resolvedVariables,
        parentVariables?.[NAME.USER_DEFINED],
      );
      if (Object.keys(userDefined).length > 0) {
        resolvedVariables[NAME.USER_DEFINED] = userDefined;
      }

      return mapExpressionVariablesByElementCategoryAfter(
        resolvedVariables,
        element,
      );
    },
    `getElementPropertiesVariables[${element.id}]`,
    [element, parentVariables, pipelineResolvedPropertyKeys],
  );
};

/**
 * Allow categories to adjust the expression variables after the regular calculation have been completed for the element.
 * Add function to element category to use
 * @param variables
 * @param element
 * @returns
 */
const mapExpressionVariablesByElementCategoryAfter = (
  variables: ExpressionVariables,
  element: OneOfElements,
) => {
  const mapFn = getElementCategory(element)?.mapExpressionVariablesAfter;
  return mapFn
    ? mapFn(variables, requiredKind(element, ElementKind.Element)) // Only IElement can have categories
    : variables;
};

/**
 * Allow categories to adjust the expression variables after the regular calculation have been completed for the element.
 * Add function to element category to use
 * @param variables
 * @param element
 * @returns
 */
const mapExpressionVariablesByElementCategoryBefore = (
  variables: ExpressionVariables,
  element: OneOfElements,
) => {
  const mapFn = getElementCategory(element)?.mapExpressionVariablesBefore;
  return mapFn
    ? mapFn(variables, requiredKind(element, ElementKind.Element)) // Only IElement can have categories
    : variables;
};

/** Inherit-placeholder expression written on quantity fallbacks (DEV-3336). */
const INHERIT_QUANTITY_PLACEHOLDER_EXPRESSION = '0';

/**
 * True when the active count source is the inherit placeholder `"0"` rather than
 * a user override. Those rows often keep an inherited {@link ExpressionValue.resolved}
 * snapshot; evaluating the literal would replace the ancestor quantity with 0.
 */
const isInheritPlaceholderFallback = (
  count: ExpressionValue,
  propertyHasUserCount: boolean,
): boolean =>
  !propertyHasUserCount &&
  count.expression.trim() === INHERIT_QUANTITY_PLACEHOLDER_EXPRESSION;

export type ExpressionVariablesWithCycles = {
  variables: ExpressionVariables;
  /** Circular reference chains that prevented resolution; empty when everything resolved. */
  cycles: string[][];
};

/**
 * Resolve each property into a named expression variable.
 * Properties without a user {@link hasCount} keep the ancestor value when the
 * active source is the inherit placeholder `"0"` whose stored resolved differs
 * from 0 (proposal-cleared quantity with a stale inherit snapshot).
 *
 * Circular references are allowed but must never fabricate values: only the
 * properties outside the loop are resolved, cycle members and their dependents
 * are cleared instead of being evaluated in insertion order (DEV-3642 — a
 * system-assigned fallback cycle made a user's mass silently resolve to 0).
 */
export const getExpressionVariablesAndCyclesFromProperties = (
  properties: IElementProperty[],
  variables?: ExpressionVariables,
  dataTables?: IDataTable[],
  pipelineResolvedPropertyKeys?: Record<string, number>,
): ExpressionVariablesWithCycles => {
  // Create new reference to variables to not modify original
  const updatedVariables = { ...(variables ?? {}) } as ExpressionVariables;
  let cycles: string[][] = [];

  try {
    // Get all properties in the order they reference eachother interally
    properties = sortPropertiesByReferences(properties);
  } catch (e) {
    if (!(e instanceof CircularReferenceError)) throw e;
    cycles = e.cycles;
    properties = e.sortedProperties;
    // A cycle member's value is unknowable — expose it as undefined rather
    // than leaking an inherited scope value or a stale snapshot.
    for (const name of e.unresolvedNames) {
      updatedVariables[name] = undefined;
    }
  }

  properties.forEach((prop) => {
    const key = formatExpressionVariableName(prop.name);
    const count = getCount(prop, false);
    const parentValue = updatedVariables.parent?.[key];
    const propertyHasUserCount = hasCount(prop);
    const shouldInherit = !propertyHasUserCount && prop.inheritFallback;

    // Don't overwrite with undefined
    if (!guards.is.defined(count)) {
      return;
    }

    const dataTableMatch = getSelectedDataTableItemFromProperty(
      prop,
      dataTables,
    );

    // For data table main properties we provide the selected json item from the json array
    if (dataTableMatch) {
      updatedVariables[dataTableMatch.name] = dataTableMatch.item;
    }

    // Inherit from parent if no count is set and the property is set to inherit fallback
    if (shouldInherit && guards.is.defined(parentValue)) {
      updatedVariables[key] = parentValue;
    }

    // Boolean & Select properties
    else if (isElementSelectProperty(prop) || isElementSwitchProperty(prop)) {
      updatedVariables[key] = getCount(prop);
    }
    // Expression properties, limit by min and max
    else if (isExpressionValue(count)) {
      // Placeholder `"0"` fallback whose stored resolved is the inherited snapshot
      // (e.g. 5) must not evaluate to 0 and shadow `parent` — grandchildren read
      // that via getParentValue after a proposal clears the local count.
      if (isInheritPlaceholderFallback(count, propertyHasUserCount)) {
        const storedResolved = count.resolved;
        if (guards.is.number(storedResolved) && storedResolved !== 0) {
          const inheritedValue = guards.is.number(parentValue)
            ? parentValue
            : storedResolved;
          updatedVariables[key] = clamp(inheritedValue, prop.min, prop.max);
          return;
        }
      }

      const pipelineResolved = pipelineResolvedPropertyKeys?.[key];
      // Compute pipeline can't fully evaluate fallbackCount expressions that read
      // ancestor scope (e.g. SBEF_LIFETIME_EXPRESSION → `building_lifetime`), so
      // re-solve here when the property is in inheritance-fallback mode. Pipeline
      // reuse remains safe for properties with an explicit `count`.
      const reusePipelineResolved =
        pipelineResolved !== undefined && !shouldInherit;
      updatedVariables[key] = reusePipelineResolved
        ? clamp(pipelineResolved, prop.min, prop.max)
        : clamp(
            errorHandledSolve(count, updatedVariables).resolved,
            prop.min,
            prop.max,
          );
    }
    // Reset fallback if parent doesn't have a value
    else if (shouldInherit && !parentValue) {
      updatedVariables[key] = undefined;
    }
  });

  return { variables: updatedVariables, cycles };
};

/** Variables-only view of {@link getExpressionVariablesAndCyclesFromProperties}. */
export const getExpressionVariablesFromProperties = (
  properties: IElementProperty[],
  variables?: ExpressionVariables,
  dataTables?: IDataTable[],
  pipelineResolvedPropertyKeys?: Record<string, number>,
): ExpressionVariables =>
  getExpressionVariablesAndCyclesFromProperties(
    properties,
    variables,
    dataTables,
    pipelineResolvedPropertyKeys,
  ).variables;

export const getVariablesById = (
  record: ExpressionVariablesRecord,
  id?: IElementID,
): ExpressionVariables => (id && record[id]) || { ...defaultVariables };

/**
 * Thrown by {@link sortPropertiesByReferences} when properties reference each
 * other in a loop. Carries enough context to resolve everything outside the
 * loop and to explain the loop to the user (DEV-3642).
 */
export class CircularReferenceError extends Error {
  constructor(
    /** Distinct dependency loops, each an ordered chain of property variable names. */
    readonly cycles: string[][],
    /** Every property variable name left unresolved — cycle members and their dependents. */
    readonly unresolvedNames: string[],
    /** Properties that still sorted, in dependency order (non-expression first). */
    readonly sortedProperties: IElementProperty[],
  ) {
    super(ExpressionErrorMessage.Circular);
    this.name = 'CircularReferenceError';
  }
}

/**
 * Extract the actual dependency loops from the unsorted leftovers of a failed
 * topological sort. Every leftover has at least one unresolved dependency, so
 * walking those edges always revisits the path (a new loop) or reaches an
 * already found loop (the leftover is a dependent, not a member).
 */
const findCyclesAmongUnresolved = (
  unresolved: Set<string>,
  depsByKey: Map<string, Set<string>>,
): string[][] => {
  const cycles: string[][] = [];
  const inFoundCycle = new Set<string>();

  for (const start of unresolved) {
    if (inFoundCycle.has(start)) continue;
    const path: string[] = [];
    const indexOnPath = new Map<string, number>();
    let current: string | undefined = start;
    while (
      current !== undefined &&
      !indexOnPath.has(current) &&
      !inFoundCycle.has(current)
    ) {
      indexOnPath.set(current, path.length);
      path.push(current);
      current = [...(depsByKey.get(current) ?? [])].find((dep) =>
        unresolved.has(dep),
      );
    }
    const cycleStart = current !== undefined && indexOnPath.get(current);
    if (guards.is.number(cycleStart)) {
      const cycle = path.slice(cycleStart);
      cycle.forEach((name) => inFoundCycle.add(name));
      cycles.push(cycle);
    }
  }
  return cycles;
};

/**
 * Find circular reference chains among the given properties. Each chain is an
 * ordered loop of property variable names, e.g. `['mass', 'area_side']` for
 * mass → area_side → mass. Empty when there is no cycle.
 */
export const findExpressionCycles = (
  properties: IElementProperty[],
): string[][] => {
  try {
    sortPropertiesByReferences(properties);
    return [];
  } catch (e) {
    if (e instanceof CircularReferenceError) return e.cycles;
    throw e;
  }
};

/** Human-readable closed loop, e.g. `mass → area_side → mass`. */
export const formatExpressionCycle = (cycle: string[]): string =>
  [...cycle, ...cycle.slice(0, 1)].join(' → ');

/** Excel-style cycle text, e.g. `Circular dependency detected: mass, area_side`. */
export const describeExpressionCycle = (cycle: string[]): string =>
  `Circular dependency detected: ${cycle.join(', ')}`;

/**
 * Make sure the order is correct so that all variables are defined when resolving.
 * If property "@b" is "@a + 10" @a must be resolved BEFORE @b.
 * Throws {@link CircularReferenceError} when references form a loop.
 * @param properties
 * @param parentVariables
 * @returns
 */
export const sortPropertiesByReferences = <
  T extends IElementProperty | IElementQuantityExpressionProperty,
>(
  properties: T[],
): T[] => {
  const expressionProperties = properties.filter(
    isElementExpressionProperty,
  ) as IElementExpressionProperty[];
  // Build local property name set for filtering out external variable references
  const localPropKeys = new Set(
    expressionProperties.map((p) => formatExpressionVariableName(p.name)),
  );

  // Parse each expression once → O(N) total parses instead of O(N²)
  const propByKey = new Map<string, IElementExpressionProperty>();
  const depsByKey = new Map<string, Set<string>>();
  const dependedOnBy = new Map<string, Set<string>>();
  const inDegree = new Map<string, number>();

  for (const prop of expressionProperties) {
    const key = formatExpressionVariableName(prop.name);
    propByKey.set(key, prop);
    dependedOnBy.set(key, dependedOnBy.get(key) ?? new Set());

    const vars = getVariablesInExpression(getCount(prop).expression);
    const localDeps = new Set<string>();

    for (const v of vars) {
      // A property referencing its own name reads the inherited outer-scope
      // value (e.g. child area_side = "area_side / 2") — not a local edge
      if (v === key) continue;
      if (localPropKeys.has(v)) localDeps.add(v);
    }

    depsByKey.set(key, localDeps);
    inDegree.set(key, localDeps.size);

    for (const dep of localDeps) {
      const set = dependedOnBy.get(dep) ?? new Set();
      set.add(key);
      dependedOnBy.set(dep, set);
    }
  }

  // Kahn's algorithm — O(V + E) topological sort
  const queue: string[] = [];
  for (const [key, deg] of inDegree) {
    if (deg === 0) queue.push(key);
  }

  const sorted: IElementExpressionProperty[] = [];

  while (queue.length > 0) {
    const key = queue.shift();
    if (key === undefined) {
      throw new Error('Expression property sort queue invariant violated');
    }
    const prop = propByKey.get(key);
    if (prop) sorted.push(prop);

    for (const dependent of dependedOnBy.get(key) ?? []) {
      const deg = (inDegree.get(dependent) ?? 1) - 1;
      inDegree.set(dependent, deg);
      if (deg === 0) queue.push(dependent);
    }
  }

  // Non-expression (switch/select) properties first, then dependency-ordered expressions
  const sortedSet = new Set<IElementProperty>(sorted);
  const isUnsortedExpression = (p: IElementProperty) =>
    !sortedSet.has(p) && propByKey.has(formatExpressionVariableName(p.name));
  const nonExpressionFirst = [
    ...properties.filter((p) => !sortedSet.has(p) && !isUnsortedExpression(p)),
    ...sorted,
  ] as T[];

  if (sorted.length !== expressionProperties.length) {
    const sortedKeys = new Set(
      sorted.map((p) => formatExpressionVariableName(p.name)),
    );
    const unresolved = [...propByKey.keys()].filter(
      (key) => !sortedKeys.has(key),
    );
    throw new CircularReferenceError(
      findCyclesAmongUnresolved(new Set(unresolved), depsByKey),
      unresolved,
      nonExpressionFirst,
    );
  }

  return nonExpressionFirst;
};

/**
 * Get all variables, first hand or by proxy, referencing this property.
 * Will crash if it detects a circular dependency
 * @param property
 * @param properties
 * @param parentVariables
 * @returns
 */
/** Build reverse index: variable name -> properties that reference it. Used by hasCircularDependency( s ). */
const buildRefsByVariable = (
  properties: IElementProperty[],
  parentVariables: ExpressionVariables,
): Map<string, IElementProperty[]> => {
  const expressionProperties = properties.filter(isElementExpressionProperty);
  const propertyNamesSet = new Set(
    expressionProperties.map((p) => formatExpressionVariableName(p.name)),
  );
  const variablesToExclude = Object.keys(parentVariables).filter(
    (v) => !isElementQuantityName(v) && !propertyNamesSet.has(v),
  );
  const refsByVar = new Map<string, IElementProperty[]>();
  for (const p of expressionProperties) {
    const vars = getVariablesInExpression(
      getCount(p).expression,
      variablesToExclude,
    );
    for (const v of vars) {
      const list = refsByVar.get(v) ?? [];
      list.push(p);
      refsByVar.set(v, list);
    }
  }
  return refsByVar;
};

/** Returns true if there is a circular dependency from the given property. Uses refsByVar from buildRefsByVariable. */
const checkCircularFrom = (
  propertyName: string,
  refsByVar: Map<string, IElementProperty[]>,
  rootKey: string,
  visited: Set<string> = new Set(),
): boolean => {
  const key = formatExpressionVariableName(propertyName);
  const refs = refsByVar.get(key) ?? [];
  // Only treat self-reference as a cycle when we're at rootKey; otherwise we'd falsely
  // report a cycle for rootKey when some other property (key) references itself.
  if (
    key === rootKey &&
    refs.some((p) => formatExpressionVariableName(p.name) === key)
  ) {
    return true;
  }
  if (
    refs.some((p) => {
      const n = formatExpressionVariableName(p.name);
      return n === rootKey || visited.has(n);
    })
  ) {
    return true;
  }
  if (refs.length === 0) return false;
  if (visited.size > 150) return true;
  visited.add(key);
  for (const ref of refs) {
    if (checkCircularFrom(ref.name, refsByVar, rootKey, visited)) {
      visited.delete(key);
      return true;
    }
  }
  visited.delete(key);
  return false;
};

/**
 * Detect if a property is referencing back to itself
 * @param propertyOrName
 * @param propertiesOrElement
 * @param modifiedExpression If you want to test if a not yet saved expression is circular
 * @returns
 */
export const hasCircularDependency = (
  propertyName: string,
  propertiesOrElement: IElementProperty[] | IElement,
  modifiedExpression?: string,
): boolean => {
  let properties = isElement(propertiesOrElement)
    ? getElementAndQuantityProperties(propertiesOrElement)
    : getElementProperties(propertiesOrElement);

  if (modifiedExpression) {
    const modifiedProperty = properties.find(
      (p) => p.name === propertyName && isElementExpressionProperty(p),
    );
    if (modifiedProperty) {
      properties = cloneReplaceItem(properties, modifiedProperty, {
        ...modifiedProperty,
        count: createExpression(modifiedExpression),
      } as IElementProperty);
    }
  }

  const refsByVar = buildRefsByVariable(properties, defaultVariables);
  const rootKey = formatExpressionVariableName(propertyName);
  return checkCircularFrom(propertyName, refsByVar, rootKey);
};

/**
 * Test if there are any circular dependencies in a list of properties
 * @param propertiesOrElement
 * @returns
 */
export const hasCircularDependencies = (
  propertiesOrElement:
    IElementProperty[] | IElement | ElementQuantityExpressionRecord,
): boolean => {
  const properties = isElement(propertiesOrElement)
    ? getElementAndQuantityProperties(propertiesOrElement)
    : getElementProperties(propertiesOrElement);

  const refsByVar = buildRefsByVariable(properties, defaultVariables);
  const expressionProperties = properties.filter(isElementExpressionProperty);
  return expressionProperties.some((p) => {
    const rootKey = formatExpressionVariableName(p.name);
    return checkCircularFrom(p.name, refsByVar, rootKey);
  });
};

/**
 * Get the gross floor area (GFA) of a building from synced {@link ProjectMetadata.design}.
 * Will always return a number greater than or equal to 1.
 * Legacy footprint fallbacks belong in {@link getLegacyBuildingGFA} (migration only).
 * @param meta Project metadata with design snapshot.
 * @returns Building GFA in m².
 */
export const getBuildingGFA = (meta: ProjectMetadata): number =>
  Math.max(1, meta.design.gfa);

export const omitDefaultVariables = (
  variables: ExpressionVariables,
): { [key: string]: number } => omit(variables, ...defaultKeys);

const getProductsFn = (
  products: ProductRecord,
  productIdOrName: string,
): IProduct | undefined => {
  const product = products[productIdOrName];
  if (!product) {
    const searchName = nameMatchTrim(productIdOrName);
    const productByName = Object.values(products).find((p) =>
      isNameMatch(p.name, searchName),
    );
    if (!productByName) {
      console.warn('Product not found', productIdOrName);
    }
    return productByName;
  }
  return product;
};

const getConversionFactorsFn = (
  getProduct: GetProductFn,
  productIdOrName: string,
): ExpressionResults => {
  const product = getProduct(productIdOrName);
  const factors = getProductConversionFactors(product);
  const genericId = product?.generic_id;

  const genericFactors = getProductConversionFactors(
    genericId ? getProduct(genericId) : undefined,
  );

  return {
    ...resultsDefaults,
    ...toExpressionConversionFactors(genericFactors),
    ...toExpressionConversionFactors(factors),
  };
};

const getProductDensityFn = (
  getConversionFactors: GetConversionFactorsFn,
  productIdOrName: string,
) => {
  const { kg, m3 } = getConversionFactors(productIdOrName);
  return productDensityFormula(kg, m3);
};

const getProductDensityPerSqmFn = (
  getConversionFactors: GetConversionFactorsFn,
  productIdOrName: string,
) => {
  const { kg, m2 } = getConversionFactors(productIdOrName);
  return productDensityPerSqmFormula(kg, m2);
};

export const productDensityFormula = (kg?: number, m3?: number): number =>
  m3 && kg ? kg / m3 : 0;

export const productDensityPerSqmFormula = (
  kg?: number,
  m2?: number,
): number => (m2 && kg ? kg / m2 : 0);

const toExpressionConversionFactors = (factors: Results): ExpressionResults =>
  Object.entries(factors).reduce((acc, [key, value]) => {
    acc[
      key
        .replaceAll(/[-/.]/g, '_')
        .replaceAll('³', '3')
        .replaceAll('²', '2') as keyof ExpressionResults
    ] = value;
    return acc;
  }, {} as ExpressionResults);

export const resultsDefaults: Readonly<ExpressionResults> =
  toExpressionConversionFactors(
    quantityUnits.reduce((acc, unit) => ({ ...acc, [unit]: 0 }), {} as Results),
  );

const getDensityRecord = (products: ProductRecord): Record<ProductID, number> =>
  mapFilterRecord(products, ({ conversion_factors }) => {
    return productDensityFormula(
      conversion_factors.kg,
      conversion_factors['m³'],
    );
  });

const getDensityPerSqmRecord = (
  products: ProductRecord,
): Record<ProductID, number> =>
  mapFilterRecord(products, ({ conversion_factors }) => {
    return productDensityPerSqmFormula(
      conversion_factors.kg,
      conversion_factors['m²'],
    );
  });
